import { remembering } from "./remembering.ts";

export const CURVE_EXPRESSION_MAX_LENGTH = 200;

type Node =
  | { readonly kind: "number"; readonly value: number }
  | { readonly kind: "level" }
  | { readonly kind: "negate"; readonly operand: Node }
  | {
      readonly kind: "binary";
      readonly operator: Operator;
      readonly left: Node;
      readonly right: Node;
    }
  | { readonly kind: "call"; readonly name: FunctionName; readonly args: readonly Node[] };

type Operator = "+" | "-" | "*" | "/" | "^";

const FUNCTIONS = {
  floor: { arity: 1, apply: (args: readonly number[]) => Math.floor(args[0]!) },
  ceil: { arity: 1, apply: (args: readonly number[]) => Math.ceil(args[0]!) },
  round: { arity: 1, apply: (args: readonly number[]) => Math.round(args[0]!) },
  abs: { arity: 1, apply: (args: readonly number[]) => Math.abs(args[0]!) },
  sqrt: { arity: 1, apply: (args: readonly number[]) => Math.sqrt(args[0]!) },
  log: { arity: 1, apply: (args: readonly number[]) => Math.log(args[0]!) },
  exp: { arity: 1, apply: (args: readonly number[]) => Math.exp(args[0]!) },
  min: { arity: 2, apply: (args: readonly number[]) => Math.min(args[0]!, args[1]!) },
  max: { arity: 2, apply: (args: readonly number[]) => Math.max(args[0]!, args[1]!) },
} as const;

type FunctionName = keyof typeof FUNCTIONS;

const isFunction = (name: string): name is FunctionName => Object.hasOwn(FUNCTIONS, name);

type Token =
  | { readonly kind: "number"; readonly value: number }
  | { readonly kind: "name"; readonly name: string }
  | { readonly kind: "symbol"; readonly symbol: Operator | "(" | ")" | "," };

const SYMBOL_ALIASES: Readonly<Record<string, Operator>> = {
  "·": "*",
  "×": "*",
  "∗": "*",
  "÷": "/",
  "−": "-",
};

const SUPERSCRIPTS: Readonly<Record<string, string>> = { "²": "2", "³": "3" };

class CurveSyntaxError extends Error {}

const tokenize = (source: string): Token[] => {
  const tokens: Token[] = [];
  let at = 0;
  while (at < source.length) {
    const rest = source.slice(at);
    const space = /^\s+/u.exec(rest);
    if (space !== null) {
      at += space[0].length;
      continue;
    }
    const number = /^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/u.exec(rest);
    if (number !== null) {
      tokens.push({ kind: "number", value: Number(number[0]) });
      at += number[0].length;
      continue;
    }
    const name = /^[A-Za-z]+/u.exec(rest);
    if (name !== null) {
      tokens.push({ kind: "name", name: name[0] });
      at += name[0].length;
      continue;
    }
    if (rest.startsWith("**")) {
      tokens.push({ kind: "symbol", symbol: "^" });
      at += 2;
      continue;
    }
    const character = rest[0]!;
    const superscript = SUPERSCRIPTS[character];
    if (superscript !== undefined) {
      tokens.push({ kind: "symbol", symbol: "^" }, { kind: "number", value: Number(superscript) });
      at += 1;
      continue;
    }
    const alias = SYMBOL_ALIASES[character];
    if (alias !== undefined) {
      tokens.push({ kind: "symbol", symbol: alias });
      at += 1;
      continue;
    }
    if ("+-*/^(),".includes(character)) {
      tokens.push({ kind: "symbol", symbol: character as Operator | "(" | ")" | "," });
      at += 1;
      continue;
    }
    throw new CurveSyntaxError(`unexpected "${character}"`);
  }
  return tokens;
};

const parse = (tokens: readonly Token[]): Node => {
  let at = 0;
  const peek = (): Token | undefined => tokens[at];
  const isSymbol = (token: Token | undefined, symbol: string): boolean =>
    token !== undefined && token.kind === "symbol" && token.symbol === symbol;
  const expect = (symbol: string): void => {
    if (!isSymbol(peek(), symbol)) throw new CurveSyntaxError(`expected "${symbol}"`);
    at += 1;
  };

  const startsFactor = (token: Token | undefined): boolean =>
    token !== undefined &&
    (token.kind === "number" || token.kind === "name" || isSymbol(token, "("));

  const atom = (): Node => {
    const token = peek();
    if (token === undefined) throw new CurveSyntaxError("the formula ends too soon");
    if (token.kind === "number") {
      at += 1;
      return { kind: "number", value: token.value };
    }
    if (token.kind === "name") {
      at += 1;
      if (token.name === "L" || token.name === "l") return { kind: "level" };
      const name = token.name.toLowerCase();
      if (!isFunction(name)) throw new CurveSyntaxError(`unknown name "${token.name}"`);
      expect("(");
      const args = [expression()];
      while (isSymbol(peek(), ",")) {
        at += 1;
        args.push(expression());
      }
      expect(")");
      if (args.length !== FUNCTIONS[name].arity) {
        throw new CurveSyntaxError(`${name} takes ${String(FUNCTIONS[name].arity)}`);
      }
      return { kind: "call", name, args };
    }
    if (isSymbol(token, "(")) {
      at += 1;
      const inner = expression();
      expect(")");
      return inner;
    }
    throw new CurveSyntaxError("expected a number, L or a bracket");
  };

  const power = (): Node => {
    const base = atom();
    if (!isSymbol(peek(), "^")) return base;
    at += 1;
    return { kind: "binary", operator: "^", left: base, right: unary() };
  };

  const unary = (): Node => {
    if (isSymbol(peek(), "-")) {
      at += 1;
      return { kind: "negate", operand: unary() };
    }
    if (isSymbol(peek(), "+")) {
      at += 1;
      return unary();
    }
    return power();
  };

  const term = (): Node => {
    let left = unary();
    for (;;) {
      const token = peek();
      if (isSymbol(token, "*") || isSymbol(token, "/")) {
        at += 1;
        const operator = isSymbol(token, "*") ? "*" : "/";
        left = { kind: "binary", operator, left, right: unary() };
      } else if (startsFactor(token)) {
        left = { kind: "binary", operator: "*", left, right: unary() };
      } else {
        return left;
      }
    }
  };

  const expression = (): Node => {
    let left = term();
    for (;;) {
      const token = peek();
      if (!isSymbol(token, "+") && !isSymbol(token, "-")) return left;
      at += 1;
      const operator = isSymbol(token, "+") ? "+" : "-";
      left = { kind: "binary", operator, left, right: term() };
    }
  };

  const tree = expression();
  if (at < tokens.length) throw new CurveSyntaxError("unexpected text after the formula");
  return tree;
};

const evaluate = (node: Node, level: number): number => {
  switch (node.kind) {
    case "number":
      return node.value;
    case "level":
      return level;
    case "negate":
      return -evaluate(node.operand, level);
    case "call":
      return FUNCTIONS[node.name].apply(node.args.map((arg) => evaluate(arg, level)));
    case "binary": {
      const left = evaluate(node.left, level);
      const right = evaluate(node.right, level);
      switch (node.operator) {
        case "+":
          return left + right;
        case "-":
          return left - right;
        case "*":
          return left * right;
        case "/":
          return left / right;
        case "^":
          return left ** right;
      }
    }
  }
};

type CurveTotal = (level: number) => number;

export type CurveReading =
  | { readonly ok: true; readonly total: CurveTotal }
  | { readonly ok: false; readonly problem: string };

export const readCurveExpression = (source: string): CurveReading => {
  if (source.trim() === "") return { ok: false, problem: "the formula is empty" };
  if (source.length > CURVE_EXPRESSION_MAX_LENGTH) {
    return { ok: false, problem: "the formula is too long" };
  }
  try {
    const tree = parse(tokenize(source));
    return { ok: true, total: (level) => evaluate(tree, level) };
  } catch (error) {
    if (error instanceof CurveSyntaxError) return { ok: false, problem: error.message };
    throw error;
  }
};

export const MAX_XP = Number.MAX_SAFE_INTEGER;

const CURVES_REMEMBERED = 32;
const remembered = remembering<Float64Array | null>(CURVES_REMEMBERED);

export const curveTotals = (source: string, levels: number): Float64Array | null =>
  remembered(`${String(levels)}:${source}`, () => {
    const reading = readCurveExpression(source);
    if (!reading.ok) return null;
    const sums = [0];
    for (let level = 1; level <= levels; level += 1) {
      const value = reading.total(level);
      if (!Number.isFinite(value)) return null;
      const total = Math.round(value);
      if (total > MAX_XP) break;
      if (total - sums[sums.length - 1]! < 1) return null;
      sums.push(total);
    }
    return Float64Array.from(sums);
  });
